import classNames from 'classnames'
import React from 'react'
import ReactDOM from 'react-dom'
import View from 'components/View'
import './index.css'
import useSelector from 'hooks/use-selector'
import logUtil from 'utils/log'
import { SIDEBAR_ID } from 'modules/sidebar/constants'
import getCurrentSidebarSelect from 'modules/sidebar/redux/selectors/get-current'
import useDispatch from 'hooks/use-dispatch'
import logEventAnalyticsUtil from 'utils/analytics/log-event'
import getActionStrReduxUtil from 'modules/redux/utils/get-action-str'
import disableHtmlScrollDomUtil from 'utils/dom/disable-html-scroll'
import enableHtmlScrollDomUtil from 'utils/dom/enable-html-scroll'
import ComponentLoader from 'components/ComponentLoader'
import runAfterDelayTimingUtil from 'utils/timing/run-after-delay'
import { ROUTE_PATH } from 'modules/route/constants'
import getPathnameLocationUtil from 'utils/location/get-pathname'
import closeSidebarAction from 'modules/sidebar/redux/actions/close'
import removeHashLocationUtil from 'utils/location/remove-hash'
import filterAndStringifyValuesObjectUtil from 'utils/object/filter-and-stringify-values'
import camelCaseToDashCaseStringUtil from 'utils/string/camel-case-to-kebab-case'
import updateOpenFromMiniCartAction from 'modules/product/redux/actions/update-open-from-mini-cart'
import wasOpenedFromMiniCartProductSelect from 'modules/product/redux/selectors/was-opened-from-mini-cart'
import getLastQuickShopHistoryItemProductsSelect from 'modules/product/redux/selectors/get-last-item-quick-shop-history'
import hasMoreThanOneQuickShopHistoryItemProductSelect from 'modules/product/redux/selectors/has-more-than-one-quick-shop-history-item'
import goBackQuickShopAction from 'containers/Sidebar/components/Content/actions/go-back'
import { getClassNameToAddSidebarContentSelect } from 'containers/Sidebar/components/Content/selectors/get-class-name-to-add'

const loadQuickShopProductSidebar = () => import('containers/QuickShopProductSidebar')
const loadMiniCartSidebar = () => import('containers/MiniCartSidebar')
const loadUnlockTierSidebar = () => import('containers/UnlockTierSidebar')
const loadReferAFriendSidebar = () => import('containers/ReferAFriendSidebar')
const loadSubscriptionsSidebar = () => import('containers/SubscriptionsSidebar')

const ROUTE_PATH_PREFIX_TO_COMPONENT_PRE_LOADERS = {
  [ROUTE_PATH.GIFT]: [
    loadQuickShopProductSidebar,
    loadMiniCartSidebar
  ],
  [ROUTE_PATH.SELF_GIFTING]: [
    loadQuickShopProductSidebar,
    loadMiniCartSidebar
  ],
  [ROUTE_PATH.SAMPLE_COLLECTION]: [
    loadQuickShopProductSidebar,
    loadMiniCartSidebar,
    loadUnlockTierSidebar
  ]
}

const SIDEBAR_TO_COMPONENT_LOADER = {
  [SIDEBAR_ID.MINI_CART]: loadMiniCartSidebar,
  [SIDEBAR_ID.QUICK_SHOP_PRODUCT]: loadQuickShopProductSidebar,
  [SIDEBAR_ID.REFER_A_FRIEND]: loadReferAFriendSidebar,
  [SIDEBAR_ID.UNLOCK_TIER]: loadUnlockTierSidebar,
  [SIDEBAR_ID.SUBSCRIPTIONS]: loadSubscriptionsSidebar
}

const ANALYTICS_EVENT = {
  OPEN_SIDEBAR: 'OPEN_SIDEBAR',
  CLOSE_SIDEBAR: 'CLOSE_SIDEBAR'
}

const DELAY_BEFORE_CLOSE_IN_MS = 300

export default function SidebarContent (props) {
  const sidebar = useSelector(getCurrentSidebarSelect)
  const id = sidebar?.id
  const [previousId, setPreviousId] = React.useState(id)
  const [isReady, setIsReady] = React.useState(false)
  const data = sidebar?.data || {}
  const title = data?.title || ''
  const dispatch = useDispatch()
  const isOpen = !!id
  const containerRef = React.useRef()
  const componentLoaderWrapper = SIDEBAR_TO_COMPONENT_LOADER[id || previousId]
  const componentLoaderFn = componentLoaderWrapper?.()

  const wasOpenedFromMiniCart = useSelector(wasOpenedFromMiniCartProductSelect)
  const classNameToAdd = useSelector(getClassNameToAddSidebarContentSelect)

  const debugProps = {
    props,
    sidebar,
    isOpen,
    previousId,
    componentLoaderFn
  }

  logUtil('SidebarContent').debug('debugProps=', debugProps)

  const hasMoreThanOneQuickShopHistoryItems = useSelector(hasMoreThanOneQuickShopHistoryItemProductSelect)
  /**
   * @type {QuickShopHistoryItem}
   */
  const lastQuickShopHistoryItem = useSelector(getLastQuickShopHistoryItemProductsSelect)
  const isProductFromMiniCart = (
    wasOpenedFromMiniCart &&
    !!lastQuickShopHistoryItem
  )

  const preLoadComponents = async () => {
    const pathname = getPathnameLocationUtil()
    const preLoaderKey = Object.keys(
      ROUTE_PATH_PREFIX_TO_COMPONENT_PRE_LOADERS
    ).find(prefix => pathname.startsWith(prefix))
    const preLoaders = ROUTE_PATH_PREFIX_TO_COMPONENT_PRE_LOADERS[preLoaderKey]

    const logPrefix = `pathname=${pathname}`
    if (!preLoaders) {
      logUtil('SidebarContent#preLoadComponents').debug(`${logPrefix} [noop] no preLoaders`)
      return
    }

    logUtil('SidebarContent#preLoadComponents').debug(`${logPrefix} preLoaders=`, preLoaders)
    await Promise.all(
      preLoaders.map(async (preLoader) => {
        if (preLoader === componentLoaderWrapper) {
          return null
        }
        await preLoader()
      })
    )
  }

  const handleReadyOnMount = async () => {
    await runAfterDelayTimingUtil()
    queueMicrotask(() => {
      setIsReady(true)
    })
  }

  const handleMount = () => {
    handleReadyOnMount()
    preLoadComponents()
  }

  React.useEffect(() => {
    handleMount()
  }, [])

  React.useEffect(() => {
    const div = document.createElement('div')
    div.className = 'sidebar-portal'
    document.body.appendChild(div)
    containerRef.current = div

    return () => {
      document.body.removeChild(div)
    }
  }, [])

  React.useEffect(() => {
    if (isOpen) {
      disableHtmlScrollDomUtil()
      logEventAnalyticsUtil(
        getActionStrReduxUtil(ANALYTICS_EVENT.OPEN_SIDEBAR, id)
      )
      return
    }
    enableHtmlScrollDomUtil()
  }, [isOpen])

  const goBack = async () => {
    dispatch(goBackQuickShopAction())
  }

  const handleClose = async (event) => {
    event.preventDefault()
    setPreviousId(id)
    removeHashLocationUtil()
    dispatch(closeSidebarAction())
    logEventAnalyticsUtil(
      getActionStrReduxUtil(ANALYTICS_EVENT.CLOSE_SIDEBAR, previousId)
    )
    await runAfterDelayTimingUtil(DELAY_BEFORE_CLOSE_IN_MS)
    removeHashLocationUtil()

    dispatch(updateOpenFromMiniCartAction({ value: false }))

    setPreviousId()
  }

  const dataId = id ? (id + '').toLowerCase() : undefined
  const className = classNames({
    sidebar: true,
    open: isOpen,
    sidebar_ready: isReady
  })
  const dataObj = {
    ...filterAndStringifyValuesObjectUtil({
      obj: data,
      str: 'data',
      keyTransformer: camelCaseToDashCaseStringUtil
    })
  }

  const hasBackButton = (
    hasMoreThanOneQuickShopHistoryItems ||
    isProductFromMiniCart
  )

  const sidebarContentClassNameObj = {
    'sidebar-content': true
  }
  if (classNameToAdd) {
    sidebarContentClassNameObj[classNameToAdd] = true
  }

  const sidebarContentClassName = classNames(sidebarContentClassNameObj)

  const content = (
    <>
      <View
        className={className}
        data-id={dataId}
        {...dataObj}
      >
        <View className='sidebar-header'>
          {
            hasBackButton
              ? (
                <View
                  className='close-icon'
                  onClick={goBack}
                >
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M21.7518 11.9998C21.7518 12.4138 21.4158 12.7498 21.0018 12.7498H4.81286L10.5328 18.4698C10.8258 18.7628 10.8258 19.2378 10.5328 19.5308C10.3868 19.6768 10.1948 19.7508 10.0028 19.7508C9.8108 19.7508 9.61877 19.6778 9.47277 19.5308L2.47277 12.5308C2.40377 12.4618 2.34891 12.3789 2.31091 12.2869C2.23491 12.1039 2.23491 11.8969 2.31091 11.7139C2.34891 11.6219 2.40377 11.5387 2.47277 11.4697L9.47277 4.46975C9.76577 4.17675 10.2408 4.17675 10.5338 4.46975C10.8268 4.76275 10.8268 5.23779 10.5338 5.53079L4.81384 11.2508H21.0018C21.4158 11.2498 21.7518 11.5858 21.7518 11.9998Z'
                      fill='#504F63'
                    />
                  </svg>
                </View>
                )
              : (
                <View
                  onClick={handleClose}
                  className='close-icon'
                >
                  <svg
                    className='close-icon'
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <path d='M18.53 17.4698C18.823 17.7628 18.823 18.2378 18.53 18.5308C18.384 18.6768 18.192 18.7508 18 18.7508C17.808 18.7508 17.616 18.6778 17.47 18.5308L12 13.0608L6.52999 18.5308C6.38399 18.6768 6.19199 18.7508 5.99999 18.7508C5.80799 18.7508 5.61599 18.6778 5.46999 18.5308C5.17699 18.2378 5.17699 17.7628 5.46999 17.4698L10.94 11.9998L5.46999 6.52981C5.17699 6.23681 5.17699 5.76177 5.46999 5.46877C5.76299 5.17577 6.238 5.17577 6.531 5.46877L12.001 10.9388L17.471 5.46877C17.764 5.17577 18.239 5.17577 18.532 5.46877C18.825 5.76177 18.825 6.23681 18.532 6.52981L13.062 11.9998L18.53 17.4698Z' />
                  </svg>
                </View>
                )
          }

          <h3 className='sidebar-header-title'>
            {title}
          </h3>

          <View className='sidebar-right-placeholder' />
        </View>

        <View className={sidebarContentClassName}>
          {
            componentLoaderFn && (
              <ComponentLoader
                loadComponent={componentLoaderFn}
                data={data}
              />
            )
          }
        </View>
      </View>
      {
        isOpen && (
          <div
            className='backdrop'
            onClick={handleClose}
          />
        )
      }
    </>
  )

  return containerRef.current
    ? ReactDOM.createPortal(content, containerRef.current)
    : null
}
